import React, { useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Chip,
  CircularProgress,
  Grid2,
  IconButton,
  Paper,
  Switch,
  Tooltip,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { observer, useLocalObservable } from "mobx-react";
import { runInAction } from "mobx";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import CustomTextField from "../../../../../views/components/common/textFields/TextField";
import AppSearchDropdownMulti from "../../../../../views/components/common/filter/AppSearchDropdownMulti";
import ConfirmationPopup from "../../../../../views/components/common/pop-up/ConfirmationPopup";
import { useDebounce } from "../../../../../views/components/common/hooks/Debounce";
import { useStores } from "../../../use-stores";
import { useNotification } from "../../../../../utils/NotificationsContext";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";
import { CategoryStore } from "../../../stores/CategoryStore";
import { CatalogOptionsStore } from "../../../stores/CatalogOptionsStore";
import { FilterValue } from "../../../../../models/Filters/FilterValue";
import {
  CategoryCreateUpdatePayload,
  CategoryOptionMapping,
  CategoryOptionMappingInput,
  getApiErrorMessage,
  normalizeCategoryCode,
  validateCategoryOptionMappings,
  validateCategoryPayload,
} from "../../../../../models/Categories/types";
import { formatDate } from "../../../../../views/components/common/functions/formatDate";

interface OptionMappingRow {
  clientKey: string;
  option_id: number | null;
  option_name: string;
  option_slug: string | null;
  option_display_type: string | null;
  display_order: string;
  is_required: number;
  active: number;
}

function mappingToRow(m: CategoryOptionMapping): OptionMappingRow {
  return {
    clientKey: `existing-${m.id}-${m.option_id}`,
    option_id: m.option_id || null,
    option_name: m.option_name || "",
    option_slug: m.option_slug,
    option_display_type: m.option_display_type,
    display_order: String(m.display_order ?? 0),
    is_required: m.is_required,
    active: m.active,
  };
}

function rowsToPayload(rows: OptionMappingRow[]): CategoryOptionMappingInput[] {
  return rows.map((r) => ({
    option_id: r.option_id || 0,
    display_order: parseInt(r.display_order, 10) || 0,
    is_required: r.is_required,
    active: r.active,
  }));
}

const CategoryDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const { userStore } = useStores();
  const { showError, showSuccess } = useNotification();
  const categoryStore = useRef(new CategoryStore()).current;
  const catalogOptionsStore = useRef(new CatalogOptionsStore()).current;

  const categoryId = Number(id);
  const canEdit = userStore.hasPermission(EmployeeRoles.EDIT_CATALOG, true);
  const colors = theme.palette.customColors;

  const page = useLocalObservable(() => ({
    loading: true,
    savingCategory: false,
    savingOptions: false,
    deleteOpen: false,
    dirtyCategory: false,
    dirtyOptions: false,
    name: "",
    code: "",
    priority: "0",
    display: 1,
    active: 1,
    has_expiry: 0,
    seoTitle: "",
    seoDescription: "",
    selectedImageUrl: "",
    unselectedImageUrl: "",
    optionRows: [] as OptionMappingRow[],
    optionSearch: "",
    optionPickerList: [] as FilterValue[],
    loadingOptionsList: false,
    hydrateCategory() {
      const d = categoryStore.categoryDetail;
      if (!d) return;
      this.name = d.name ?? "";
      this.code = d.code ?? "";
      this.priority = String(d.priority ?? 0);
      this.display = d.display;
      this.active = d.active;
      this.has_expiry = d.has_expiry ?? 0;
      this.seoTitle = d.seo_title ?? "";
      this.seoDescription = d.seo_description ?? "";
      this.selectedImageUrl = d.selected_image_url ?? "";
      this.unselectedImageUrl = d.unselected_image_url ?? "";
      this.dirtyCategory = false;
    },
    hydrateOptions() {
      this.optionRows = (categoryStore.categoryOptions ?? []).map(mappingToRow);
      this.dirtyOptions = false;
    },
    markCategoryDirty() {
      this.dirtyCategory = true;
    },
    markOptionsDirty() {
      this.dirtyOptions = true;
    },
    addOptionRow() {
      this.optionRows = [
        ...this.optionRows,
        {
          clientKey: `new-${Date.now()}`,
          option_id: null,
          option_name: "",
          option_slug: null,
          option_display_type: null,
          display_order: String(this.optionRows.length + 1),
          is_required: 1,
          active: 1,
        },
      ];
      this.dirtyOptions = true;
    },
    removeOptionRow(clientKey: string) {
      this.optionRows = this.optionRows.filter(
        (r) => r.clientKey !== clientKey,
      );
      this.dirtyOptions = true;
    },
    updateOptionRow(
      clientKey: string,
      patch: Partial<Omit<OptionMappingRow, "clientKey">>,
    ) {
      const index = this.optionRows.findIndex(
        (row) => row.clientKey === clientKey,
      );
      if (index < 0) return;

      const current = this.optionRows[index];
      const nextRow: OptionMappingRow = {
        clientKey: current.clientKey,
        option_id: current.option_id,
        option_name: current.option_name,
        option_slug: current.option_slug,
        option_display_type: current.option_display_type,
        display_order: current.display_order,
        is_required: Number(current.is_required) === 1 ? 1 : 0,
        active: Number(current.active) === 1 ? 1 : 0,
        ...patch,
      };

      this.optionRows = this.optionRows.map((row, i) =>
        i === index ? nextRow : row,
      );
      this.dirtyOptions = true;
    },
    toggleOptionFlag(clientKey: string, field: "is_required" | "active") {
      const current = this.optionRows.find(
        (row) => row.clientKey === clientKey,
      );
      if (!current) return;
      this.updateOptionRow(clientKey, {
        [field]: Number(current[field]) === 1 ? 0 : 1,
      });
    },
  }));

  const debouncedOptionSearch = useDebounce(page.optionSearch, 500);

  const loadDetail = async () => {
    if (!Number.isFinite(categoryId) || categoryId <= 0) {
      showError("Error", "Invalid category id.");
      navigate("/categories/listing");
      return;
    }
    try {
      page.loading = true;
      await Promise.all([
        categoryStore.getCategoryById(categoryId),
        categoryStore.getCategoryOptions(categoryId),
      ]);
      runInAction(() => {
        page.hydrateCategory();
        page.hydrateOptions();
      });
    } catch (error) {
      showError("Error", getApiErrorMessage(error, "Failed to load category."));
      navigate("/categories/listing");
    } finally {
      page.loading = false;
    }
  };

  useEffect(() => {
    void loadDetail();
    return () => {
      categoryStore.clearCategoryDetail();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId]);

  const loadOptionPicker = async (search?: string) => {
    try {
      page.loadingOptionsList = true;
      const response = await catalogOptionsStore.listOptions({
        page_no: 1,
        limit: 50,
        active: 1,
        search: search?.trim() || undefined,
        sort_by: "name",
      });
      runInAction(() => {
        page.optionPickerList = (response.list ?? []).map((o) => ({
          id: String(o.id),
          value: o.slug ? `${o.name} (${o.slug})` : o.name,
        }));
      });
    } catch (error) {
      showError(
        "Error",
        getApiErrorMessage(error, "Failed to load catalog options."),
      );
    } finally {
      page.loadingOptionsList = false;
    }
  };

  useEffect(() => {
    if (!canEdit) return;
    void loadOptionPicker(debouncedOptionSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedOptionSearch, canEdit]);

  const detail = categoryStore.categoryDetail;

  const buildCategoryPayload = (): CategoryCreateUpdatePayload => ({
    name: page.name.trim(),
    code: normalizeCategoryCode(page.code),
    priority: (() => {
      const n = parseInt(page.priority, 10);
      return Number.isNaN(n) ? 0 : n;
    })(),
    display: page.display,
    active: page.active,
    has_expiry: page.has_expiry,
    seo_title: page.seoTitle.trim() || null,
    seo_description: page.seoDescription.trim() || null,
    selected_image_url: page.selectedImageUrl.trim() || null,
    unselected_image_url: page.unselectedImageUrl.trim() || null,
  });

  const handleSaveCategory = async () => {
    if (!canEdit || !detail || page.savingCategory) return;
    const payload = buildCategoryPayload();
    const validationError = validateCategoryPayload(payload);
    if (validationError) {
      showError("Validation", validationError);
      return;
    }
    try {
      page.savingCategory = true;
      await categoryStore.updateCategoryRecord(categoryId, payload);
      runInAction(() => page.hydrateCategory());
    } catch (error) {
      showError(
        "Error",
        getApiErrorMessage(error, "Could not update category."),
      );
    } finally {
      page.savingCategory = false;
    }
  };

  const handleSaveOptions = async () => {
    if (!canEdit || page.savingOptions) return;
    const payload = rowsToPayload(page.optionRows);
    const validationError = validateCategoryOptionMappings(payload);
    if (validationError) {
      showError("Validation", validationError);
      return;
    }
    try {
      page.savingOptions = true;
      await categoryStore.putCategoryOptions(categoryId, { options: payload });
      runInAction(() => page.hydrateOptions());
    } catch (error) {
      showError(
        "Error",
        getApiErrorMessage(error, "Could not update category options."),
      );
    } finally {
      page.savingOptions = false;
    }
  };

  const handleDeleteCategory = async () => {
    if (!canEdit || !detail || page.savingCategory) return;
    try {
      page.savingCategory = true;
      await categoryStore.deleteCategoryRecord(detail.id);
      showSuccess("Category soft-deleted successfully.");
      page.deleteOpen = false;
      navigate("/categories/listing");
    } catch (error) {
      showError(
        "Error",
        getApiErrorMessage(error, "Failed to delete category."),
      );
    } finally {
      page.savingCategory = false;
    }
  };

  const sectionSx = {
    p: { xs: 2, sm: 2.5 },
    borderRadius: 2,
    border: 1,
    borderColor: "divider",
    bgcolor: "background.paper",
    mb: 2,
  };

  if (page.loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!detail) {
    return (
      <Box sx={{ p: 4 }}>
        <CustomTypography variant="heading6" color={colors?.errorRed}>
          Category not found.
        </CustomTypography>
      </Box>
    );
  }

  const selectedOptionIds = new Set(
    page.optionRows
      .map((r) => r.option_id)
      .filter((v): v is number => typeof v === "number" && v > 0),
  );

  return (
    <Grid2 size={{ xs: 12 }}>
      <Paper
        sx={{
          marginTop: "0.6rem",
          paddingX: { xs: 2, sm: 4 },
          paddingY: 2,
          boxShadow:
            "rgba(0, 0, 0, 0.25) 0px 0.0625em 0.0625em, rgba(0, 0, 0, 0.25) 0px 0.125em 0.5em, rgba(255, 255, 255, 0.1) 0px 0px 0px 1px inset",
          borderRadius: "4px",
          mb: 2,
        }}
      >
        <Grid2
          container
          spacing={2}
          columns={12}
          alignItems="flex-start"
          sx={{ mb: 1 }}
        >
          <Grid2 size={{ xs: 12, md: 8 }}>
            <Grid2 container spacing={1} columns={12} alignItems="flex-start">
              <Grid2 size="auto">
                <IconButton
                  size="small"
                  onClick={() => navigate("/categories/listing")}
                  sx={{ mt: 0.25 }}
                >
                  <ArrowBackIcon />
                </IconButton>
              </Grid2>
              <Grid2 size="grow">
                <CustomTypography variant="heading3">
                  {detail.name}
                </CustomTypography>
                <Grid2 container spacing={1} sx={{ mt: 0.75 }}>
                  <Grid2 size="auto">
                    <Chip
                      size="small"
                      label={detail.active === 1 ? "Active" : "Inactive"}
                      color={detail.active === 1 ? "success" : "default"}
                      variant={detail.active === 1 ? "filled" : "outlined"}
                    />
                  </Grid2>
                  <Grid2 size="auto">
                    <Chip
                      size="small"
                      label={detail.display === 1 ? "Displayed" : "Hidden"}
                      variant="outlined"
                    />
                  </Grid2>
                </Grid2>
                <CustomTypography
                  variant="body3"
                  color={colors?.greyTextColor}
                  sx={{ mt: 0.75 }}
                >
                  Category #{detail.id}
                  {detail.code ? ` · Code ${detail.code}` : ""}
                  {detail.modified_date
                    ? ` · Updated ${formatDate(detail.modified_date)}`
                    : ""}
                  {page.dirtyCategory || page.dirtyOptions
                    ? " · Unsaved changes"
                    : ""}
                </CustomTypography>
              </Grid2>
            </Grid2>
          </Grid2>

          {canEdit ? (
            <Grid2 size={{ xs: 12, md: 4 }}>
              <Grid2
                container
                spacing={1}
                columns={12}
                justifyContent={{ xs: "flex-start", md: "flex-end" }}
              >
                <Grid2 size="auto">
                  <AppButton
                    variant="transparentPrimary"
                    label="Delete"
                    onClick={() => {
                      page.deleteOpen = true;
                    }}
                    disabled={detail.active !== 1 || page.savingCategory}
                  />
                </Grid2>
                <Grid2 size="auto">
                  <AppButton
                    variant="primary"
                    label={page.savingCategory ? "Saving…" : "Save category"}
                    onClick={() => void handleSaveCategory()}
                    disabled={page.savingCategory || !page.dirtyCategory}
                  />
                </Grid2>
              </Grid2>
            </Grid2>
          ) : null}
        </Grid2>
      </Paper>

      <Paper elevation={0} sx={sectionSx}>
        <CustomTypography variant="heading6" sx={{ mb: 0.5 }}>
          Category settings
        </CustomTypography>
        <CustomTypography
          variant="body3"
          color={colors?.greyTextColor}
          sx={{ mb: 2.5 }}
        >
          Update name, code, priority, flags, and SEO. Changes apply when you
          save.
        </CustomTypography>

        <Grid2 container spacing={2} columns={12}>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <CustomTypography
              variant="body3"
              color={colors?.greyTextColor}
              sx={{ mb: 0.75 }}
            >
              Name *
            </CustomTypography>
            <CustomTextField
              width="100%"
              placeholder="Category name"
              value={page.name}
              disabled={!canEdit || page.savingCategory}
              onChange={(e) => {
                page.name = e.target.value || "";
                page.markCategoryDirty();
              }}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 3 }}>
            <CustomTypography
              variant="body3"
              color={colors?.greyTextColor}
              sx={{ mb: 0.75 }}
            >
              Product code *
            </CustomTypography>
            <CustomTextField
              width="100%"
              placeholder="POL"
              value={page.code}
              disabled={!canEdit || page.savingCategory}
              onChange={(e) => {
                page.code = (e.target.value || "").toUpperCase();
                page.markCategoryDirty();
              }}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 3 }}>
            <CustomTypography
              variant="body3"
              color={colors?.greyTextColor}
              sx={{ mb: 0.75 }}
            >
              Priority
            </CustomTypography>
            <CustomTextField
              width="100%"
              type="number"
              allowNegativeValue={false}
              allowDecimalValue={false}
              placeholder="0"
              value={page.priority}
              disabled={!canEdit || page.savingCategory}
              onChange={(e) => {
                page.priority = e.target.value || "0";
                page.markCategoryDirty();
              }}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 4 }}>
            <Box display="flex" alignItems="center" gap={1}>
              <Switch
                checked={page.active === 1}
                disabled={!canEdit || page.savingCategory}
                onChange={(_, checked) => {
                  page.active = checked ? 1 : 0;
                  page.markCategoryDirty();
                }}
              />
              <CustomTypography variant="body2">
                {page.active === 1 ? "Active" : "Inactive"}
              </CustomTypography>
            </Box>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 4 }}>
            <Box display="flex" alignItems="center" gap={1}>
              <Switch
                checked={page.display === 1}
                disabled={!canEdit || page.savingCategory}
                onChange={(_, checked) => {
                  page.display = checked ? 1 : 0;
                  page.markCategoryDirty();
                }}
              />
              <CustomTypography variant="body2">
                {page.display === 1 ? "Displayed" : "Hidden"}
              </CustomTypography>
            </Box>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 4 }}>
            <Box display="flex" alignItems="center" gap={1}>
              <Switch
                checked={page.has_expiry === 1}
                disabled={!canEdit || page.savingCategory}
                onChange={(_, checked) => {
                  page.has_expiry = checked ? 1 : 0;
                  page.markCategoryDirty();
                }}
              />
              <CustomTypography variant="body2">Has expiry</CustomTypography>
            </Box>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <CustomTypography
              variant="body3"
              color={colors?.greyTextColor}
              sx={{ mb: 0.75 }}
            >
              Selected image URL
            </CustomTypography>
            <CustomTextField
              width="100%"
              placeholder="https://"
              value={page.selectedImageUrl}
              disabled={!canEdit || page.savingCategory}
              onChange={(e) => {
                page.selectedImageUrl = e.target.value || "";
                page.markCategoryDirty();
              }}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <CustomTypography
              variant="body3"
              color={colors?.greyTextColor}
              sx={{ mb: 0.75 }}
            >
              Unselected image URL
            </CustomTypography>
            <CustomTextField
              width="100%"
              placeholder="https://"
              value={page.unselectedImageUrl}
              disabled={!canEdit || page.savingCategory}
              onChange={(e) => {
                page.unselectedImageUrl = e.target.value || "";
                page.markCategoryDirty();
              }}
            />
          </Grid2>
          <Grid2 size={12}>
            <CustomTypography
              variant="body3"
              color={colors?.greyTextColor}
              sx={{ mb: 0.75 }}
            >
              SEO title
            </CustomTypography>
            <CustomTextField
              width="100%"
              placeholder="SEO title"
              value={page.seoTitle}
              disabled={!canEdit || page.savingCategory}
              onChange={(e) => {
                page.seoTitle = e.target.value || "";
                page.markCategoryDirty();
              }}
            />
          </Grid2>
          <Grid2 size={12}>
            <CustomTypography
              variant="body3"
              color={colors?.greyTextColor}
              sx={{ mb: 0.75 }}
            >
              SEO description
            </CustomTypography>
            <CustomTextField
              width="100%"
              placeholder="SEO description"
              value={page.seoDescription}
              disabled={!canEdit || page.savingCategory}
              onChange={(e) => {
                page.seoDescription = e.target.value || "";
                page.markCategoryDirty();
              }}
            />
          </Grid2>
        </Grid2>
      </Paper>

      <Paper elevation={0} sx={sectionSx}>
        <Grid2
          container
          spacing={1.5}
          columns={12}
          alignItems="center"
          sx={{ mb: 2 }}
        >
          <Grid2 size={{ xs: 12, sm: "grow" }}>
            <CustomTypography variant="heading6">
              Option mappings
            </CustomTypography>
            <CustomTypography variant="body3" color={colors?.greyTextColor}>
              {page.optionRows.length} option
              {page.optionRows.length === 1 ? "" : "s"} mapped for SKU
              generation
            </CustomTypography>
          </Grid2>
          {canEdit ? (
            <Grid2 size={{ xs: 12, sm: "auto" }}>
              <Grid2 container spacing={1}>
                <Grid2 size="auto">
                  <AppButton
                    variant="transparentPrimary"
                    label="Add option"
                    startIcon={<AddIcon />}
                    onClick={() => page.addOptionRow()}
                    disabled={page.savingOptions}
                  />
                </Grid2>
                <Grid2 size="auto">
                  <AppButton
                    variant="primary"
                    label={page.savingOptions ? "Saving…" : "Save options"}
                    onClick={() => void handleSaveOptions()}
                    disabled={page.savingOptions || !page.dirtyOptions}
                  />
                </Grid2>
              </Grid2>
            </Grid2>
          ) : null}
        </Grid2>

        {page.optionRows.length === 0 ? (
          <Box
            sx={{
              py: 4,
              textAlign: "center",
              borderRadius: 2,
              border: `1px dashed ${colors?.greyBorder || theme.palette.divider}`,
            }}
          >
            <CustomTypography variant="body2" color={colors?.greyTextColor}>
              No options mapped yet.
              {canEdit ? " Add an option to configure SKU generation." : ""}
            </CustomTypography>
          </Box>
        ) : (
          <Grid2 container spacing={2} columns={12}>
            {page.optionRows.map((row) => {
              const optionList: FilterValue[] = [
                ...page.optionPickerList.filter(
                  (o) =>
                    !selectedOptionIds.has(Number(o.id)) ||
                    Number(o.id) === row.option_id,
                ),
              ];
              if (
                row.option_id &&
                !optionList.some((o) => Number(o.id) === row.option_id)
              ) {
                optionList.unshift({
                  id: String(row.option_id),
                  value: row.option_name || `Option #${row.option_id}`,
                });
              }

              return (
                <Grid2 key={row.clientKey} size={12}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: `1px solid ${colors?.greyBorder || theme.palette.divider}`,
                      bgcolor: alpha(
                        colors?.purpleMain || theme.palette.primary.main,
                        0.02,
                      ),
                    }}
                  >
                    <Grid2
                      container
                      spacing={2}
                      columns={12}
                      alignItems="center"
                    >
                      <Grid2 size={{ xs: 12, md: 4 }}>
                        <CustomTypography
                          variant="body3"
                          color={colors?.greyTextColor}
                          sx={{ mb: 0.75 }}
                        >
                          Option *
                        </CustomTypography>
                        {canEdit ? (
                          <AppSearchDropdownMulti
                            title="Select option"
                            isMultiSelect={false}
                            local={false}
                            list={optionList}
                            value={
                              row.option_id
                                ? optionList.filter(
                                    (o) => Number(o.id) === row.option_id,
                                  )
                                : []
                            }
                            disabled={page.savingOptions}
                            borderColor={colors?.blackBorder}
                            borderRadius="8px"
                            handleSearch={(text) => {
                              page.optionSearch = text || "";
                            }}
                            handleSelect={(items) => {
                              const selected = items[0];
                              if (!selected) return;
                              const optionId = Number(selected.id);
                              const fromStore =
                                catalogOptionsStore.options.find(
                                  (o) => o.id === optionId,
                                );
                              page.updateOptionRow(row.clientKey, {
                                option_id: optionId,
                                option_name: fromStore?.name || selected.value,
                                option_slug: fromStore?.slug ?? null,
                                option_display_type:
                                  fromStore?.display_type != null
                                    ? String(fromStore.display_type)
                                    : null,
                              });
                            }}
                          />
                        ) : (
                          <CustomTypography variant="body2" fontWeight={600}>
                            {row.option_name || "—"}
                          </CustomTypography>
                        )}
                        {row.option_display_type ? (
                          <Chip
                            size="small"
                            label={row.option_display_type}
                            variant="outlined"
                            sx={{
                              mt: 1,
                              height: 22,
                              fontSize: "0.7rem",
                              textTransform: "capitalize",
                            }}
                          />
                        ) : null}
                      </Grid2>
                      <Grid2 size={{ xs: 6, md: 2 }}>
                        <CustomTypography
                          variant="body3"
                          color={colors?.greyTextColor}
                          sx={{ mb: 0.75 }}
                        >
                          Display order
                        </CustomTypography>
                        <CustomTextField
                          width="100%"
                          type="number"
                          allowNegativeValue={false}
                          allowDecimalValue={false}
                          value={row.display_order}
                          disabled={!canEdit || page.savingOptions}
                          onChange={(e) => {
                            page.updateOptionRow(row.clientKey, {
                              display_order: e.target.value || "0",
                            });
                          }}
                        />
                      </Grid2>
                      <Grid2 size={{ xs: 12, md: 6 }}>
                        <Box
                          display="flex"
                          alignItems="center"
                          flexWrap="wrap"
                          gap={1.5}
                          justifyContent={{ xs: "flex-start", md: "flex-end" }}
                        >
                          <Box
                            display="inline-flex"
                            alignItems="center"
                            gap={0.5}
                            onClick={() => {
                              if (!canEdit || page.savingOptions) return;
                              page.toggleOptionFlag(
                                row.clientKey,
                                "is_required",
                              );
                            }}
                            sx={{
                              cursor:
                                !canEdit || page.savingOptions
                                  ? "default"
                                  : "pointer",
                              userSelect: "none",
                              opacity: !canEdit || page.savingOptions ? 0.6 : 1,
                            }}
                          >
                            <Switch
                              checked={Number(row.is_required) === 1}
                              size="small"
                              disabled={!canEdit || page.savingOptions}
                              onChange={() => {}}
                              sx={{ pointerEvents: "none" }}
                            />
                            <CustomTypography variant="body3">
                              Required
                            </CustomTypography>
                          </Box>
                          <Box
                            display="inline-flex"
                            alignItems="center"
                            gap={0.5}
                            onClick={() => {
                              if (!canEdit || page.savingOptions) return;
                              page.toggleOptionFlag(row.clientKey, "active");
                            }}
                            sx={{
                              cursor:
                                !canEdit || page.savingOptions
                                  ? "default"
                                  : "pointer",
                              userSelect: "none",
                              opacity: !canEdit || page.savingOptions ? 0.6 : 1,
                            }}
                          >
                            <Switch
                              checked={Number(row.active) === 1}
                              size="small"
                              disabled={!canEdit || page.savingOptions}
                              onChange={() => {}}
                              sx={{ pointerEvents: "none" }}
                            />
                            <CustomTypography variant="body3">
                              Active
                            </CustomTypography>
                          </Box>
                          {canEdit ? (
                            <Tooltip title="Remove mapping">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    page.removeOptionRow(row.clientKey)
                                  }
                                  disabled={page.savingOptions}
                                  sx={{ color: colors?.errorRed }}
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          ) : null}
                        </Box>
                      </Grid2>
                    </Grid2>
                  </Box>
                </Grid2>
              );
            })}
          </Grid2>
        )}
        {page.loadingOptionsList ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
            <CircularProgress size={18} />
          </Box>
        ) : null}
      </Paper>

      <Paper
        elevation={0}
        sx={{
          ...sectionSx,
          bgcolor: (t) => alpha(t.palette.grey[500], 0.06),
          mb: 0,
        }}
      >
        <CustomTypography variant="heading7" sx={{ mb: 2 }}>
          Metadata
        </CustomTypography>
        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <CustomTypography
              variant="body3"
              color={colors?.greyTextColor}
              sx={{ mb: 0.5 }}
            >
              Created
            </CustomTypography>
            <CustomTypography variant="body2" fontWeight={600}>
              {formatDate(detail.created_date)}
            </CustomTypography>
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <CustomTypography
              variant="body3"
              color={colors?.greyTextColor}
              sx={{ mb: 0.5 }}
            >
              Modified
            </CustomTypography>
            <CustomTypography variant="body2" fontWeight={600}>
              {formatDate(detail.modified_date)}
            </CustomTypography>
          </Grid2>
        </Grid2>
      </Paper>

      {page.deleteOpen ? (
        <ConfirmationPopup
          id={detail.id}
          open={page.deleteOpen}
          title="Delete Category"
          content={`Soft-delete "${detail.name}"? You can restore it later from the listing.`}
          onConfirm={() => {
            void handleDeleteCategory();
          }}
          onCancel={() => {
            page.deleteOpen = false;
          }}
        />
      ) : null}
    </Grid2>
  );
};

export default observer(CategoryDetailPage);
